<template>
  <hr-form-dialog
    ref="dialog"
    size="lg"
    :title="treatmentServiceTitle"
    @close="onCloseDialog"
    @submit="onSubmitForm"
  >
    <div class="row q-col-gutter-md">
      <p
        id="service-appointment-conflict-message"
        class="col-12 q-ma-none"
      >
        {{ treatmentServiceSubTitle }}
      </p>
      <hr-option-group
        v-model="appointmentsAction"
        aria-labelledby="service-appointment-conflict-message"
        :options="[
          { value: 'delete-appointments', label: $t('authorizations.editAuthorization.deleteAllAppointments') },
          { value: 'remove-service', label: $t('authorizations.editAuthorization.removeServiceFromAppointments') },
        ]"
        class="col-12"
      />
      <hr-data-table
        :rows="appointments"
        :columns="columns"
        class="col-12"
      />
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="onCancel"
      />
      <hr-confirm-btn
        type="submit"
        :label="$t('authorizations.editAuthorization.buttonSaveAndUpdate')"
        :disable="!appointmentsAction"
      />
    </template>
  </hr-form-dialog>
</template>

<script>
import { Timestamp } from 'firebase/firestore';
import { isNumber } from 'lodash';
import { useTime } from 'src/composables/time';
import {
  HrConfirmBtn,
  HrDataTable,
  HrDismissBtn,
  HrFormDialog,
  HrOptionGroup,
} from 'src/design-system';

export default {
  name: 'ServiceAppointmentConflictDialog',

  components: {
    HrConfirmBtn,
    HrDataTable,
    HrDismissBtn,
    HrFormDialog,
    HrOptionGroup,
  },

  props: {
    treatmentService: {
      type: String,
      required: true,
    },

    appointments: {
      type: Array,
      required: true,
    },

    mode: {
      type: String,
      required: true,
      validator(value) {
        return ['edit', 'delete'].includes(value);
      },
    },

  },

  emits: ['hide', 'ok'],

  setup() {
    const { getLocaleDateString, getLocaleTimeString, addToDate } = useTime();
    return { getLocaleDateString, getLocaleTimeString, addToDate };
  },

  data() {
    return {
      appointmentsAction: null,

      columns: [
        {
          name: 'userName',
          label: this.$t('appointments.tableCols.user'),
          align: 'left',
          field: 'userName',
        },
        {
          name: 'scheduledStartDate',
          label: this.$t('appointments.tableCols.scheduledStartDate'),
          align: 'left',
          field: 'scheduledStartTimestamp',
          format: (scheduledStartTimestamp) => (
            scheduledStartTimestamp instanceof Timestamp ? this.getLocaleDateString(scheduledStartTimestamp) : ''
          ),
        },
        {
          name: 'scheduledStartTimestamp',
          label: this.$t('appointments.tableCols.scheduledStartTime'),
          align: 'left',
          field: 'scheduledStartTimestamp',
          format: (scheduledStartTimestamp) => (
            scheduledStartTimestamp instanceof Timestamp ? this.getLocaleTimeString(scheduledStartTimestamp) : ''
          ),
        },
        {
          name: 'scheduledEndTimestamp',
          group: null,
          label: this.$t('appointments.tableCols.scheduledEndTime'),
          align: 'left',
          field: 'duration',
          format: (duration, { scheduledStartTimestamp }) => {
            const calcTime = this.calculateEndTime(scheduledStartTimestamp, duration);
            return calcTime?.toDate() ? this.getLocaleTimeString(calcTime) : '';
          },
        },
        {
          name: 'sessionName',
          label: this.$t('appointments.tableCols.sessionName'),
          align: 'left',
          field: 'sessionName',
        },
        {
          name: 'status',
          label: this.$t('history.tableCols.exportStatus'),
          field: 'status',
          align: 'left ',
        },
      ],
    };
  },

  computed: {
    treatmentServiceTitle() {
      return this.$t(
        `authorizations.editAuthorization.${this.mode === 'edit' ? 'editing' : 'deleting'}TreatmentService`,
        { treatmentService: this.treatmentService },
      );
    },

    treatmentServiceSubTitle() {
      return this.$t(
        `authorizations.editAuthorization.${this.mode === 'edit'
          ? 'authAppointmentsConflictMessage'
          : 'unAuthAppointmentsConflictMessage'
        }`,
      );
    },
  },

  methods: {

    show() {
      this.$refs.dialog.show();
    }, // show()

    hide() {
      this.$emit('hide');

      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    }, // hide()

    onSubmitForm() {
      this.$emit('ok', this.appointmentsAction);
      this.hide();
    }, // onSubmitForm()

    onCloseDialog() {
      this.onCancel();
    },

    onCancel() {
      this.hide();
    },

    calculateEndTime(scheduledStartTimestamp, duration) {
      if (!(scheduledStartTimestamp instanceof Timestamp) || !isNumber(duration)) {
        return undefined;
      }
      return Timestamp.fromDate(
        this.addToDate(scheduledStartTimestamp.toDate(), { seconds: duration }),
      );
    }, // calculateEndTime()

  },
};
</script>
